import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const FAQPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "What is LuvCup?",
          a: "LuvCup is an AI-powered relationship assessment platform that helps you discover your love language, understand relationship patterns, and gain personalized insights through interactive quizzes."
        },
        {
          q: "How do I get started?",
          a: "Simply create a free account, verify your email, and start taking any of our 5 relationship tests. Each test takes 5-7 minutes and provides personalized AI-generated insights."
        },
        {
          q: "Is LuvCup free?",
          a: "Yes! LuvCup offers a free plan with access to 5 basic relationship tests and AI insights. For unlimited retakes, advanced features, PDF reports, and couples matching, we offer premium plans starting at $4.99/month (or $39/year - save $20.88). See our pricing page for full details."
        },
        {
          q: "What are the pricing plans?",
          a: "We offer 3 plans: FREE ($0 - 5 basic tests, basic AI insights, limited retakes), POPULAR ($4.99/month or $39/year - unlimited retakes, advanced AI insights, PDF reports, couples matching), and PREMIUM ($9.99/month or $79/year - everything plus AI chat, personalized courses, exclusive community access). All premium plans include a 7-day free trial."
        }
      ]
    },
    {
      category: "Taking Tests",
      questions: [
        {
          q: "Can I skip questions?",
          a: "Yes! If you're uncomfortable answering any question, you can click the 'Skip' button. The AI will generate insights based on the questions you do answer."
        },
        {
          q: "Can I save my progress?",
          a: "Absolutely! Click 'Save & Continue Later' at any time during a quiz. When you return, you'll see a prompt to resume from where you left off."
        },
        {
          q: "Are my quiz answers private?",
          a: "Yes, your responses are completely confidential. They're only used to generate your personalized insights and are never shared without your permission (except in Couples Mode when you explicitly invite a partner)."
        },
        {
          q: "How accurate are the AI insights?",
          a: "Our AI uses advanced GPT-4 technology trained on relationship psychology principles. While insights are highly personalized, they're for educational purposes and shouldn't replace professional counseling."
        }
      ]
    },
    {
      category: "Couples Mode",
      questions: [
        {
          q: "What is Couples Mode?",
          a: "Couples Mode allows you to invite your partner to take the same test. You'll receive a compatibility score and personalized discussion topics based on both your results."
        },
        {
          q: "How do I invite my partner?",
          a: "Go to your Dashboard, click 'Couples Mode', enter your partner's email, select a test, and send the invitation. They'll receive an email with a unique link."
        },
        {
          q: "Can my partner see my individual results?",
          a: "Yes, in Couples Mode both partners can view each other's scores and insights to foster open communication. Each partner must consent by accepting the invitation."
        }
      ]
    },
    {
      category: "Account & Privacy",
      questions: [
        {
          q: "Why do I need to verify my email?",
          a: "Email verification ensures account security and allows us to send you important notifications, PDF reports, and partner invitations."
        },
        {
          q: "Can I delete my account?",
          a: "Yes, you can request account deletion by contacting support@luvcup.app. We'll permanently delete your data within 30 days."
        },
        {
          q: "How is my data protected?",
          a: "We use industry-standard encryption (HTTPS, bcrypt password hashing) and never sell your data. Read our Privacy Policy for full details."
        },
        {
          q: "What data do you collect?",
          a: "We collect: account info (name, email), quiz responses, optional demographics, and usage data to improve our services. All data collection is transparent in our Privacy Policy."
        }
      ]
    },
    {
      category: "Features & Functionality",
      questions: [
        {
          q: "What's the AI Chat Assistant?",
          a: "After completing a test, you can chat with our AI assistant to ask questions about your results, get clarification on insights, or receive additional relationship advice."
        },
        {
          q: "Can I retake tests?",
          a: "Yes! You can retake any test as many times as you like. This helps you track how your relationship perspectives evolve over time."
        },
        {
          q: "How do I download my results?",
          a: "On your results page, click 'Download PDF Report' to get a beautifully formatted document with your insights, score, and personalized recommendations."
        },
        {
          q: "Does LuvCup work on mobile?",
          a: "Yes! LuvCup is fully mobile-responsive. You can even swipe left/right to navigate quiz questions on touch devices."
        }
      ]
    },
    {
      category: "Payment & Billing",
      questions: [
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure payment processor Stripe. All payments are encrypted and PCI-compliant."
        },
        {
          q: "Can I cancel my subscription anytime?",
          a: "Yes! You can cancel your subscription at any time from your account settings. Your premium features will remain active until the end of your billing period. No cancellation fees."
        },
        {
          q: "Do you offer refunds?",
          a: "We offer a 7-day free trial for all premium plans. If you're not satisfied within the trial period, you won't be charged. After that, we offer case-by-case refunds within 14 days of purchase. Contact support@luvcup.app to request a refund."
        },
        {
          q: "Will my subscription auto-renew?",
          a: "Yes, subscriptions automatically renew at the end of each billing cycle (monthly or yearly) to ensure uninterrupted access. You can cancel anytime before renewal to avoid charges."
        },
        {
          q: "Can I switch between monthly and yearly billing?",
          a: "Yes! You can upgrade to yearly billing at any time to save money. Changes to your billing cycle take effect at your next renewal date. Contact support@luvcup.app for assistance."
        }
      ]
    },
    {
      category: "Commission Program",
      questions: [
        {
          q: "What is the LuvCup Commission Program?",
          a: "The LuvCup Commission Program allows you to earn 25% commission by referring new users to our Popular or Premium subscription plans. Share your love for LuvCup and get rewarded!"
        },
        {
          q: "How do I join the Commission Program?",
          a: "Visit our Commission Program application page, fill out the required information (including your contact details and why you want to participate), agree to the terms and conditions, and submit your application. Our team will review and approve qualified applicants."
        },
        {
          q: "What commission rate do I earn?",
          a: "You earn 25% commission on every Popular and Premium subscription sale made through your unique referral link. Free trials, cancellations before payment, and downgraded subscriptions do not qualify."
        },
        {
          q: "How do I get paid?",
          a: "Commissions are paid monthly/bi-weekly/quarterly once you reach the minimum payout threshold. Payments are made via PayPal or bank transfer. You'll need to provide accurate payment information in your commission dashboard."
        },
        {
          q: "How do I track my referrals and earnings?",
          a: "Once approved, you'll receive access to a dedicated Commission Partner Dashboard where you can view your unique referral link, track referrals, see commission earnings (pending and paid), and monitor your performance."
        },
        {
          q: "What are the requirements to participate?",
          a: "You must be at least 17 years old, have a valid LuvCup account in good standing, and agree to follow our marketing guidelines. You cannot make false claims, use spam tactics, or promote on inappropriate websites."
        },
        {
          q: "Can I use my referral link on social media?",
          a: "Yes! You can share your referral link on social media, blogs, email campaigns, and other marketing channels as long as you follow our marketing guidelines and don't make misleading claims about LuvCup services."
        },
        {
          q: "What happens if someone cancels their subscription?",
          a: "Commissions are only paid on confirmed, paid subscriptions. If a user cancels before payment or requests a refund, the commission will be adjusted or withheld accordingly."
        }
      ]
    },
    {
      category: "Technical Issues",
      questions: [
        {
          q: "I didn't receive my verification email",
          a: "Check your spam folder first. If still not there, go to the login page and click 'Resend verification email' or contact support@luvcup.app."
        },
        {
          q: "I forgot my password",
          a: "Click 'Forgot Password' on the login page, enter your email, and we'll send you a reset link."
        },
        {
          q: "The website isn't loading properly",
          a: "Try clearing your browser cache, disabling browser extensions, or using a different browser. If the issue persists, contact our support team."
        },
        {
          q: "Can I use LuvCup on multiple devices?",
          a: "Yes! Your account syncs across all devices. Just log in with your email and password on any device."
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen hero-bg px-6 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-rose-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <HelpCircle className="w-12 h-12 text-rose-600 mr-3" />
            <h1 className="text-4xl md:text-5xl font-bold gradient-text">Frequently Asked Questions</h1>
          </div>
          <p className="text-xl text-gray-600">
            Find answers to common questions about LuvCup
          </p>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="glass-card p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">{category.category}</h2>
              
              <div className="space-y-4">
                {category.questions.map((faq, questionIndex) => {
                  const index = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openIndex === index;
                  
                  return (
                    <div key={questionIndex} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
                      <button
                        onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                        className="w-full flex items-center justify-between text-left py-3 hover:text-rose-600 transition-colors"
                      >
                        <span className="font-semibold text-gray-800 pr-4">{faq.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-rose-600 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      
                      {isOpen && (
                        <div className="mt-2 text-gray-600 leading-relaxed animate-fadeIn">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions? */}
        <div className="glass-card p-8 mt-8 text-center bg-gradient-to-br from-rose-50 to-pink-50">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">Still Have Questions?</h3>
          <p className="text-gray-600 mb-6">
            Can't find what you're looking for? We're here to help!
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="btn-primary"
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;